import React from 'react';
import ContactForm from '../components/ContactForm';
import { Mail, Phone, MapPin, Clock, Globe } from 'lucide-react';

const ContactPage: React.FC = () => {
  return (
    <>
      <section className="py-16 md:py-24 bg-gradient-to-r from-blue-900 to-indigo-600">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <span className="inline-block px-3 py-1 rounded-full bg-white/20 text-white text-sm font-medium mb-4 backdrop-blur-sm">
            Contact Us
          </span>
          <h1 className="text-2xl md:text-5xl font-bold mb-6 text-white">
            Let's Discuss Your Project
          </h1>
          <p className="md:text-xl text-blue-100 max-w-3xl mx-auto">
            Ready to transform your business with technology and AI? Get in touch with our team of experts to discuss your project needs.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-12">
            <div className="lg:w-2/5">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">
                Contact Information
              </h2>
              <p className="text-gray-600 mb-8">
                Have questions or need more information? Our team is here to help. Reach out to us through any of the channels below.
              </p>

              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                      <Mail size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900">Email Us</h3>
                    <p className="text-gray-600">
                      <a href="mailto:info@technostacklabs.com" className="hover:text-blue-600 transition duration-300">
                        info@technostacklabs.com
                      </a>
                    </p>
                    <p className="text-gray-600">
                      <a href="mailto:support@technostacklabs.com" className="hover:text-blue-600 transition duration-300">
                        support@technostacklabs.com
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                      <Phone size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900">Call Us</h3>
                    <p className="text-gray-600">
                      <a href="tel:+11234567890" className="hover:text-blue-600 transition duration-300">
                        +91 9167784169
                      </a>
                    </p>
                    <p className="text-gray-600">
                      <a href="tel:+11987654321" className="hover:text-blue-600 transition duration-300">
                        +91 9810312223
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                      <MapPin size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900">Visit Us</h3>
                    <p className="text-gray-600">
                    Plot No. D-107, Vyapar Marg,<br />
                    D-Block, Sector 2, Noida, Uttar Pradesh 201301
                    </p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                      <Clock size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-gray-900">Working Hours</h3>
                    <p className="text-gray-600">
                      Monday - Friday: 9:00 AM - 6:00 PM<br />
                      Saturday, Sunday: Closed
                    </p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                      <Globe size={24} />
                    </div>
                  </div>
                  <div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:w-3/5">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>


      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-600 mb-12">
              Find answers to common questions about our services and process.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    What is the typical timeline for a project?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    Project timelines vary depending on the scope and complexity of the project. A simple website might take 4-6 weeks, while a complex enterprise application or AI solution could take several months. During our initial consultation, we'll provide a detailed timeline based on your specific requirements.
                  </p>
                </div>
              </details>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    How much does it cost to develop a custom solution?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    The cost of custom development depends on various factors including the complexity of the solution, desired features, and timeline. We offer flexible pricing models including fixed-price, time and materials, and retainer options. Contact us for a free consultation and personalized quote.
                  </p>
                </div>
              </details>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    Do you provide ongoing maintenance and support?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    Yes, we offer comprehensive maintenance and support packages to ensure your solution continues to run smoothly after launch. Our support plans include regular updates, security patches, performance monitoring, and technical assistance. We can tailor a support plan to meet your specific needs and budget.
                  </p>
                </div>
              </details>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    What technology stack do you use for development?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    We work with a variety of technologies and select the best stack for each project based on your specific requirements. We're technology-agnostic and focus on choosing the right tools for your needs.
                  </p>
                </div>
              </details>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex items-center justify-between p-6 cursor-pointer bg-white hover:bg-gray-50 transition duration-300">
                  <h3 className="md:text-lg font-semibold text-gray-900">
                    How do you ensure the quality and security of your solutions?
                  </h3>
                  <span className="ml-6 flex-shrink-0 text-gray-400 group-open:rotate-180 transition duration-300">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </span>
                </summary>
                <div className="px-6 pb-6 text-gray-600">
                  <p>
                    Quality and security are our top priorities. We implement rigorous quality assurance processes including automated testing, code reviews, and manual testing. For security, we follow industry best practices including secure coding standards, regular security audits, and compliance with relevant regulations. We also provide documentation and training to ensure your team understands how to maintain security protocols.
                  </p>
                </div>
              </details>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
